/**
 * TeamSystem Impact - Engagement API Client
 * API calls for stakeholder engagement campaign management
 */

import { apiClient } from '../../api/client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type StakeholderCategory =
  | 'dipendenti'
  | 'investitori'
  | 'clienti'
  | 'fornitori'
  | 'comunita_locali'
  | 'regolatori'
  | 'ong'
  | 'sindacati'
  | 'altro';

export type EngagementCampaignStatus = 'active' | 'closed';

export type EngagementCampaignType = 'agentic' | 'manual';

export type EngagementSource = 'mcp' | 'web';

export interface Campaign {
  _id: string;
  aziendaId: string;
  name: string;
  description: string;
  targetCategory: StakeholderCategory;
  type: EngagementCampaignType;
  status: EngagementCampaignStatus;
  assessmentYear: number;
  isDefault: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface Entry {
  _id: string;
  campaignId: string;
  aziendaId: string;
  userId: string;
  userName: string;
  userEmail: string;
  comment: string;
  agentId?: string;
  agentName?: string;
  source: EngagementSource;
  createdAt: string;
}

export interface Stats {
  totalEntries: number;
  entriesLast7Days: number;
  entriesLast30Days: number;
  byCampaign: Array<{
    campaignId: string;
    campaignName: string;
    count: number;
  }>;
}

export interface CreateCampaignDTO {
  name: string;
  description?: string;
  targetCategory: StakeholderCategory;
  assessmentYear: number;
}

export interface FetchEntriesParams {
  campaignId?: string;
  limit?: number;
  offset?: number;
  year?: number;
}

export interface FetchEntriesResult {
  entries: Entry[];
  total: number;
}

// ---------------------------------------------------------------------------
// API response wrapper (same pattern as data-capture)
// ---------------------------------------------------------------------------

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

const BASE_PATH = '/engagement';

export const engagementApi = {
  fetchCampaigns: async (year?: number): Promise<Campaign[]> => {
    const query = year ? `?year=${year}` : '';
    const response = await apiClient.get<ApiResponse<Campaign[]>>(
      `${BASE_PATH}/campaigns${query}`,
    );
    if (!response.data.success)
      throw new Error(response.data.error || 'Failed to fetch campaigns');
    return response.data.data!;
  },

  createCampaign: async (dto: CreateCampaignDTO): Promise<Campaign> => {
    const response = await apiClient.post<ApiResponse<Campaign>>(
      `${BASE_PATH}/campaigns`,
      dto,
    );
    if (!response.data.success)
      throw new Error(response.data.error || 'Failed to create campaign');
    return response.data.data!;
  },

  updateCampaignStatus: async (
    id: string,
    status: EngagementCampaignStatus,
  ): Promise<Campaign> => {
    const response = await apiClient.patch<ApiResponse<Campaign>>(
      `${BASE_PATH}/campaigns/${id}/status`,
      { status },
    );
    if (!response.data.success)
      throw new Error(response.data.error || 'Failed to update campaign status');
    return response.data.data!;
  },

  fetchEntries: async (
    params: FetchEntriesParams = {},
  ): Promise<FetchEntriesResult> => {
    const query = new URLSearchParams();
    if (params.campaignId) query.append('campaignId', params.campaignId);
    if (params.limit != null) query.append('limit', String(params.limit));
    if (params.offset != null) query.append('offset', String(params.offset));
    if (params.year != null) query.append('year', String(params.year));

    const qs = query.toString();
    const response = await apiClient.get<ApiResponse<FetchEntriesResult>>(
      `${BASE_PATH}/entries${qs ? `?${qs}` : ''}`,
    );
    if (!response.data.success)
      throw new Error(response.data.error || 'Failed to fetch entries');
    return response.data.data!;
  },

  fetchStats: async (year?: number): Promise<Stats> => {
    const query = year ? `?year=${year}` : '';
    const response = await apiClient.get<ApiResponse<Stats>>(
      `${BASE_PATH}/stats${query}`,
    );
    if (!response.data.success)
      throw new Error(response.data.error || 'Failed to fetch stats');
    return response.data.data!;
  },
};
